import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import type { BookmarkCheck, BookmarkableType } from '@/types/bookmark/Bookmark'
import type { UUID } from '@/types/Id'

import DataLoader from '@/vendor/dataloader'

/** Maximum number of bookmark checks in a single API request */
const MAX_BATCH_SIZE = 50

interface BulkCheckItem {
  bookmarkable_type: BookmarkableType
  object_id: string
}

interface BulkCheckResponse {
  results: BookmarkCheck[]
}

const api = {
  bulkCheck: async (
    orgId: UUID,
    checks: BulkCheckItem[],
    accessToken: string,
  ): Promise<BulkCheckResponse> => {
    const res = await fetch(
      `${API_URL}/api/v1/organizations/${orgId}/bookmarks/check/bulk`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ checks }),
      },
    )

    if (!res.ok) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: res.statusText,
        cause: { status: res.status },
      })
    }

    return (await res.json()) as BulkCheckResponse
  },
}

/** Parse a composite DataLoader key back into its parts. */
const parseKey = (key: string): BulkCheckItem => {
  const idx = key.indexOf(':')
  return {
    bookmarkable_type: key.slice(0, idx) as BookmarkableType,
    object_id: key.slice(idx + 1),
  }
}

const NOT_BOOKMARKED: BookmarkCheck = {
  is_bookmarked: false,
  bookmark_id: null,
}

const batchBookmarkChecks = async (
  keys: readonly string[],
  orgId: UUID,
  accessToken: string,
): Promise<(BookmarkCheck | Error)[]> => {
  const checks = keys.map(parseKey)
  const response = await api.bulkCheck(orgId, checks, accessToken)

  return keys.map((_key, i) => response.results[i] ?? NOT_BOOKMARKED)
}

export const createBookmarkCheckLoader = (orgId: UUID, accessToken: string) => {
  return new DataLoader<string, BookmarkCheck>(
    (keys) => batchBookmarkChecks(keys, orgId, accessToken),
    {
      cache: false,
      maxBatchSize: MAX_BATCH_SIZE,
    },
  )
}
